/// <amd-module name="Pipeline/Analytics/PipelineCardMetrics/Pipeline.CardMetrics" />
import { AnalyticsExtensionValidator } from "Analytics/Shared/Common/AnalyticsExtensionValidator";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { BuildDefinition } from "Build/Client/Contracts/Build";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import * as Resources from "Pipeline/Analytics/PipelineCardMetrics/Resources";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, IVssComponentProperties, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

export interface IBuildSampleDefinitionDetailProps extends IVssComponentProperties {
    onDefinitionChanged: (handler: (definition: BuildDefinition | undefined) => void) => void;
}

export interface IBuildSampleDefinitionDetailState {
    definition: BuildDefinition | undefined;
}

export class PipelineCardMetrics extends VssComponent<IBuildSampleDefinitionDetailProps, IBuildSampleDefinitionDetailState> {
    constructor(props: IBuildSampleDefinitionDetailProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            definition: undefined
        };

        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }
    }

    public async componentDidMount() {
        this.props.onDefinitionChanged(this._onDefinitionChanged);
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <AnalyticsExtensionValidator>{this._renderComponent()}</AnalyticsExtensionValidator>
            </Surface>
        );
    }

    private _renderComponent(): JSX.Element {
        if (this.state.definition) {
            const childComponentProperties = {
                definitionId: this.state.definition.id,
                contextType: "build",
                onCardClick: this._loadRoute
            };

            return (
                <RegionComponent className="flex-row" componentRegion="pipelineAnalyticsRegion" childComponentProperties={childComponentProperties} />
            );
        } else {
            return <Spinner className="analytics-card-spinner flex-grow" size={SpinnerSize.large} />;
        }
    }

    private _loadRoute = (routeId: string, event?: any): void => {
        if (this.state.definition && this.state.definition.id > 0) {
            const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                const project = tfsPageData.project.name;
                const url = routeUrl(this.context.pageContext, routeId, {
                    project: project,
                    definitionId: this.state.definition.id.toString(),
                    contextType: "build"
                });
                if (url) {
                    onClickFPS(this.context.pageContext, url, true, event);
                }
            } else {
                throw new Error(Resources.ProjectEmptyErrorText);
            }
        }
    };

    private _onDefinitionChanged = (definition: BuildDefinition | undefined) => {
        this.setState({
            definition: definition
        });

        if (definition) {
            const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
            telemetryService.initialize(definition.id);
            telemetryService.publishEventWithSingleKey(TelemetryConstants.feature_AnalyticsTab, TelemetryConstants.ClickCount, 1);
        }
    };
}

Components.add("pipelineCardMetrics", PipelineCardMetrics);
